import React from 'react';
import { Box, Typography } from '@mui/material';
import { getArchLayout, type Dentition, type ToothFace } from '../../constants/odontology';
import { getToothView, type ToothViewMap } from '../../utils/dentalChartView';
import ToothGlyph from './ToothGlyph';

/**
 * Arcadas completas em notação FDI. A hemiarcada direita do paciente aparece à
 * esquerda na tela, como se o examinador estivesse de frente para ele.
 */

export interface DentalChartCanvasProps {
  dentition: Dentition;
  viewMap: ToothViewMap;
  selectedTooth?: string | null;
  selectedFaces?: ToothFace[];
  /** Mostra o perfil lateral de cada dente além da vista oclusal. */
  showLateral?: boolean;
  disabled?: boolean;
  onSelectTooth?: (toothNumber: string) => void;
  onToggleFace?: (toothNumber: string, face: ToothFace) => void;
}

const MidlineMark: React.FC = () => (
  <Box
    aria-hidden
    sx={{
      width: '1px',
      alignSelf: 'stretch',
      mx: { xs: 0.5, sm: 1 },
      background: 'linear-gradient(180deg, transparent, #CBD5E1 20%, #CBD5E1 80%, transparent)',
    }}
  />
);

const DentalChartCanvas: React.FC<DentalChartCanvasProps> = ({
  dentition,
  viewMap,
  selectedTooth,
  selectedFaces = [],
  showLateral = false,
  disabled = false,
  onSelectTooth,
  onToggleFace,
}) => {
  const arch = getArchLayout(dentition);

  const renderRow = (teeth: string[], numberPosition: 'top' | 'bottom') => (
    <Box sx={{ display: 'flex', gap: { xs: 0.25, sm: 0.5 } }}>
      {teeth.map((tooth) => (
        <ToothGlyph
          key={tooth}
          toothNumber={tooth}
          view={getToothView(viewMap, tooth)}
          selected={selectedTooth === tooth}
          selectedFaces={selectedTooth === tooth ? selectedFaces : []}
          numberPosition={numberPosition}
          showLateral={showLateral}
          disabled={disabled}
          onSelectTooth={onSelectTooth}
          onToggleFace={onToggleFace}
        />
      ))}
    </Box>
  );

  const renderArch = (
    row: { right: string[]; left: string[] },
    label: string,
    numberPosition: 'top' | 'bottom',
  ) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography
        sx={{
          fontSize: '0.65rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: '#94A3B8',
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
        {renderRow(row.right, numberPosition)}
        <MidlineMark />
        {renderRow(row.left, numberPosition)}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        overflowX: 'auto',
        overflowY: 'hidden',
        py: 1,
        // Papel de prontuário: grade clínica discreta sob as arcadas.
        backgroundImage:
          'linear-gradient(#F1F5F9 1px, transparent 1px), linear-gradient(90deg, #F1F5F9 1px, transparent 1px)',
        backgroundSize: '24px 24px',
        borderRadius: 2,
        border: '1px solid #E2E8F0',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: 2, sm: 3 },
          minWidth: dentition === 'permanent' ? 620 : 420,
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          width: 'fit-content',
          mx: 'auto',
        }}
      >
        {renderArch(arch.upper, 'Arcada superior', 'top')}
        {renderArch(arch.lower, 'Arcada inferior', 'bottom')}
      </Box>
    </Box>
  );
};

export default DentalChartCanvas;
